import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCorrieri, deleteCorriere, exportCorrieriCSV, deleteMultipleCorrieri, exportCorrieriExcel, getLayoutsByModulo, generatePdf } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, Badge, Container } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { usePageTitle } from '../context/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faShippingFast, faPhone, faEuroSign, faUser, faPlus, faFileExport, faPrint } from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortController from '../common/SortController';
import SortableHeader from '../common/SortableHeader';
import ColumnOrderController from '../common/ColumnOrderController';

const columnHelper = createColumnHelper();

const allColumnsMap = {
    id: "ID",
    denominazione: "Denominazione",
    telefono: "Telefono",
    cellulare: "Cellulare",
    tipo_spedizione: "Tipo Spedizione",
    costo: "Costo (storico)",
    costo_ivato: "Costo ivato",
    sconto_ricarica_pct: "Sconto ricariche %",
    costo_netto_effettivo: "Costo netto",
    referente: "Referente",
    mirakl_carrier_code: "Codice Corriere Mirakl",
    banca_pagamento_id: "ID Banca Pagamento",
    paccofacile_service_id: "Service ID PaccoFacile",
};

const initialVisibleColumns = ['denominazione', 'telefono', 'tipo_spedizione', 'costo_ivato', 'costo_netto_effettivo'];

const visibilityKey = 'table-visibility-corrieri';

function CorrieriList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [corrieri, setCorrieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCorriereIds, setSelectedCorriereIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [printLayouts, setPrintLayouts] = useState([]);
  const [quickSearch, setQuickSearch] = useState(() => sessionStorage.getItem('list-quicksearch-corrieri') || '');

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-corrieri');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-corrieri');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-corrieri');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => c !== 'select');
        return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-corrieri');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-corrieri', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-corrieri', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-quicksearch-corrieri', quickSearch);
    const timer = setTimeout(() => {
      setColumnFilters(prev => {
        const without = prev.filter(f => f.id !== '_global_search');
        return quickSearch ? [...without, { id: '_global_search', value: quickSearch }] : without;
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  useEffect(() => {
    localStorage.setItem('table-column-order-corrieri', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-corrieri', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalCorrieri, setTotalCorrieri] = useState(0);

  const fetchCorrieriData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = JSON.stringify(
        columnFilters.reduce((acc, filter) => {
          const { id, value } = filter;
          if (value && typeof value === 'object' && !Array.isArray(value) && value.__advanced) {
            if (value.__advanced === 'not_empty') acc[`${id}__isnot`] = null;
            else if (value.__advanced === 'contains') acc[`${id}__ilike`] = value.text;
            else if (value.__advanced === 'not_contains') acc[`${id}__not_ilike`] = value.text;
          }
          else if (Array.isArray(value) && value.length > 0) acc[id] = { 'in': value };
          else if (typeof value === 'string' && value.toUpperCase() === 'N/A') acc[id] = null;
          else acc[id] = value;
          return acc;
        }, {})
      );
      // getCorrieri vuole un singolo order_by/direction, non un array: senza questo il
      // click sull'intestazione per ordinare non avrebbe mai avuto alcun effetto (l'array
      // interpolato in query string diventava una stringa tipo "denominazione:desc" che
      // non corrisponde a nessun campo reale del modello).
      const order_by = sorting[0]?.id || null;
      const direction = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : null;
      const response = await getCorrieri((currentPage - 1) * itemsPerPage, itemsPerPage, order_by, direction, filters);
      setCorrieri(response.data.corrieri);
      setTotalCorrieri(response.data.total);
    } catch (err) {
      setError('Errore nel caricamento dei corrieri.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  useEffect(() => {
    fetchCorrieriData();
  }, [fetchCorrieriData]);

  useEffect(() => {
    const fetchLayouts = async () => {
        try {
            const response = await getLayoutsByModulo('Corriere');
            setPrintLayouts(response.data);
        } catch (error) {
            console.error("Errore nel caricamento dei layout di stampa", error);
        }
    };
    fetchLayouts();
  }, []);

  const getColumnValue = (corriere, columnName) => {
    return corriere[columnName] != null ? String(corriere[columnName]) : 'N/A';
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={corrieri.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => getColumnValue(info.row.original, key),
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        })
      ),
    ],
    [corrieri]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: corrieri, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true,
  });

  const handleAutoResize = (column) => {
    const headerText = String(column.columnDef.header);
    const cells = table.getRowModel().rows.map(row => String(row.original[column.id] ?? ''));
    const charMultiplier = 8, padding = 30;
    const headerWidth = headerText.length * charMultiplier;
    const maxWidth = cells.reduce((max, cell) => Math.max(max, cell.length * charMultiplier), headerWidth);
    const newSize = Math.ceil(maxWidth) + padding;
    table.setColumnSizing(prev => ({ ...prev, [column.id]: newSize }));
  };

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);
    setSelectedCorriereIds(newSelectedIds);
  }, [rows]);

  const handleRowClick = (row) => row.toggleSelected();

  const createDownloadLink = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    setExporting(true);
    try {
      const response = await exportCorrieriCSV(selectedCorriereIds);
      createDownloadLink(response, 'corrieri_selezionati.csv');
      table.toggleAllRowsSelected(false);
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExporting(false); }
  };

  const handleExportAllExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportCorrieriExcel();
      createDownloadLink(response, 'corrieri.xlsx');
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExportingExcel(false); }
  };

  const handlePrintWithLayout = async (layoutId) => {
    if (selectedCorriereIds.length === 0) return alert("Seleziona almeno un corriere per la stampa.");
    setLoading(true);
    try {
      const response = await generatePdf(layoutId, selectedCorriereIds);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento_layout_${layoutId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { setError("Errore durante la generazione del PDF."); }
    finally { setLoading(false); }
  };

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
    setQuickSearch('');
  };

  const deleteApiMap = { corrieri: deleteCorriere };
  const deleteMultipleApiMap = { corrieri: deleteMultipleCorrieri };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalCorrieri / itemsPerPage);
    const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
    if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
    else {
      if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
      else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
      else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return pageNumbers;
  };

  const hasManualFilters = table.getState().columnFilters.length > 0;

  // Render function per le card mobile
  const renderCorriereCard = useCallback((corriere, isSelected, onToggleSelect) => {
    return (
      <div className="d-flex align-items-start">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect && onToggleSelect(e)}
          className="me-3"
          style={{ marginTop: '2px' }}
        />
        
        <div className="flex-grow-1" onClick={() => navigate(`/corrieri/edit/${corriere.id}`)} style={{ cursor: 'pointer' }}>
          <div className="d-flex align-items-center mb-1">
            <FontAwesomeIcon icon={faTruck} className="text-primary me-2" />
            <h6 className="mb-0 fw-bold">{corriere.denominazione || 'Corriere senza denominazione'}</h6>
          </div>
          
          <small className="text-muted d-block mb-2">
            ID: {corriere.id}
            {corriere.tipo_spedizione && ` • ${corriere.tipo_spedizione}`}
          </small>
          
          <div>
            {corriere.telefono && (
              <Badge bg="secondary" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faPhone} className="me-1" />
                {corriere.telefono}
              </Badge>
            )}
            {corriere.costo && (
              <Badge bg="primary" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faEuroSign} className="me-1" />
                €{corriere.costo}
              </Badge>
            )}
            {corriere.tipo_spedizione && (
              <Badge bg="info" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faShippingFast} className="me-1" />
                {corriere.tipo_spedizione}
              </Badge>
            )}
            {corriere.referente && (
              <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faUser} className="me-1" />
                {corriere.referente}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );;
  }, [navigate]);

  // Quick filters per mobile (CorrieriList non ha filtri specifici)
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faTruck,
      isActive: true,
      onClick: () => {},
      gradients: ['#6c757d', '#495057']
    }
  ], []);

  // Actions dropdown content
  const actionsDropdownContent = useMemo(() => (
    <>
      <div className="px-3 py-2 border-bottom">
        <small className="text-muted fw-bold">ESPORTAZIONE</small>
      </div>
      <Dropdown.Item onClick={handleExportSelected} disabled={selectedCorriereIds.length === 0 || exporting}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}
      </Dropdown.Item>
      
      <div className="px-3 py-2 border-bottom border-top">
        <small className="text-muted fw-bold">STAMPA</small>
      </div>
      {Array.isArray(printLayouts) && printLayouts.map((layout) => (
        <Dropdown.Item key={layout.id} onClick={() => handlePrintWithLayout(layout.id)} disabled={selectedCorriereIds.length === 0}>
          <FontAwesomeIcon icon={faPrint} className="me-2" />
          {layout.nome}
        </Dropdown.Item>
      ))}
    </>
  ), [exporting, exportingExcel, selectedCorriereIds.length, printLayouts, handleExportSelected, handleExportAllExcel, handlePrintWithLayout]);

  const [searchValue, setSearchValue] = useState('');
  
  const toggleAllSelected = () => {
    const allSelected = selectedCorriereIds.length === table.getRowModel().rows.length;
    if (allSelected) {
      table.toggleAllRowsSelected(false);
    } else {
      table.toggleAllRowsSelected(true);
    }
  };

  const toggleCorriereSelection = (id) => {
    const row = table.getRowModel().rows.find(row => row.original.id === id);
    if (row) {
      row.toggleSelected();
    }
  };

  const handleClearFilters = () => {
    table.resetColumnFilters();
    setSearchValue('');
  };

  usePageTitle(`Corrieri (${totalCorrieri})`);

  return (
    <>
      <Container fluid>
        {isMobile ? (
          <MobileListLayout
            entityName="corriere"
            entityNamePlural="corrieri"
            createPath="/nuovo/corriere"
            createButtonText="Nuovo Corriere"
            items={table.getRowModel().rows.map(row => row.original)}
            renderCard={renderCorriereCard}
            quickFilters={quickFilters}
            onSearchChange={(e) => {
              const value = e.target.value;
              setSearchValue(value);
              table.getColumn('denominazione')?.setFilterValue(value);
            }}
            searchPlaceholder="Cerca corrieri..."
            selectedIds={selectedCorriereIds}
            onToggleSelect={(id) => {
              const row = table.getRowModel().rows.find(row => row.original.id === id);
              if (row) row.toggleSelected();
            }}
            onSelectAll={() => {
              const allSelected = selectedCorriereIds.length === table.getRowModel().rows.length;
              table.toggleAllRowsSelected(!allSelected);
            }}
            onCardClick={(corriere) => navigate(`/corrieri/edit/${corriere.id}`)}
            onDeleteMultiple={async (ids) => {
              if (window.confirm(`Vuoi eliminare ${ids.length} corriere/i selezionato/i?`)) {
                await deleteMultipleCorrieri(ids);
                fetchCorrieriData();
                table.toggleAllRowsSelected(false);
              }
            }}
            actionsDropdownContent={actionsDropdownContent}
          />
        ) : (
          <Card>
            <Card.Header className="sticky-card-header">
              <Row className="align-items-center">
                <Col md={6} className="d-flex justify-content-start align-items-center">
                  <Button as={Link} to="/nuovo/corriere" className="me-2" size="sm" variant="success">Nuovo Corriere</Button>
                  <Dropdown className="me-2">
                    <Dropdown.Toggle variant="info" id="dropdown-export" size="sm">Esporta</Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleExportSelected} disabled={selectedCorriereIds.length === 0 || exporting}>{exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}</Dropdown.Item>
                      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>{exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  {printLayouts.length > 0 && (
                    <Dropdown className="me-2">
                      <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Array.isArray(printLayouts) && printLayouts.map(layout => (
                          <Dropdown.Item key={layout.id} onClick={() => handlePrintWithLayout(layout.id)} disabled={selectedCorriereIds.length === 0}>Stampa {layout.nome}</Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </Col>
                <Col md={6} className="d-flex justify-content-end align-items-center">
                  <Dropdown className="me-2" autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">Ordinamento</Dropdown.Toggle>
                    <Dropdown.Menu className="long-dropdown-menu p-2">
                      <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown className="me-2" autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
                    <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                      <div className="d-flex">
                        <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                        <div className="p-2" style={{ flex: 1 }}>
                          <h6 className="fw-bold">Mostra Colonne</h6>
                          <div className="d-flex flex-column">
                            {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                          </div>
                        </div>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters && sorting.length === 0}>Azzera Filtri</Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Form.Control
                type="text"
                className="mb-3"
                placeholder="Cerca per denominazione, referente, tipo spedizione..."
                value={quickSearch}
                onChange={e => setQuickSearch(e.target.value)}
              />
              {loading && <div className="text-center"><Spinner animation="border" /></div>}
              {error && <Alert variant="danger">{error}</Alert>}
              {!loading && !error && (
                <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                  <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => <SortableHeader key={header.id} header={header} table={table} handleAutoResize={handleAutoResize} />)}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <ActionToolbar selectedItemIds={selectedCorriereIds} entityType="corrieri" onDeleteSuccess={fetchCorrieriData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} />
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <Form.Group as={Row} className="align-items-center mb-0">
                <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
                <Col xs="auto">
                  <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                    <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Form.Group>
              <Pagination size="sm" className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
                {renderPaginationItems()}
                <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalCorrieri / itemsPerPage)} />
                <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalCorrieri / itemsPerPage))} disabled={currentPage === Math.ceil(totalCorrieri / itemsPerPage)} />
              </Pagination>
            </Card.Footer>
          </Card>
        )}
      </Container>

</>
  );
}

export default CorrieriList;